import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import qrTagSvg from '../assets/brand/QR Tag VL.svg';

// SVG template viewBox: 198.43 × 368.5
// QR placeholder area in SVG: x=33.27, y=204.77, w=131.85, h=132.28
// Using scale=10 → canvas 1984.3 × 3685 px for high quality
const TEMPLATE_VB_W = 198.43;
const TEMPLATE_VB_H = 368.5;
const QR_PLACEHOLDER_X = 33.27;
const QR_PLACEHOLDER_Y = 204.77;
const QR_PLACEHOLDER_W = 131.85;
const QR_PLACEHOLDER_H = 132.28;

const getStableFrontendUrl = () => {
  if (process.env.REACT_APP_FRONTEND_URL) return process.env.REACT_APP_FRONTEND_URL.trim();
  const hostname = window.location.hostname;
  if (['app.sideo.in', 'sideo.in', 'www.sideo.in', 'sideo-pre-qr.vercel.app'].includes(hostname))
    return 'https://app.sideo.in';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return window.location.origin;
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

/**
 * Build a branded QR card canvas by compositing the SVG template + generated QR code.
 * scale multiplies the SVG viewBox units to pixels.
 * scale=10 → 1701×3118 px (good quality); scale=6 → lighter PDFs.
 */
const buildBrandedCanvas = async (qrId, scale = 10) => {
  const stableUrl = getStableFrontendUrl();
  const qrUrl = `${stableUrl}/q/${qrId}`;

  const canvasW = Math.round(TEMPLATE_VB_W * scale);
  const canvasH = Math.round(TEMPLATE_VB_H * scale);
  const qrPx = Math.round(QR_PLACEHOLDER_W * scale);
  const qrPxH = Math.round(QR_PLACEHOLDER_H * scale);

  const qrDataUrl = await QRCode.toDataURL(qrUrl, {
    errorCorrectionLevel: 'H',
    type: 'image/png',
    quality: 0.95,
    margin: 0,
    width: Math.max(qrPx, qrPxH),
    color: { dark: '#000000', light: '#FFFFFF' },
  });

  const [templateImg, qrImg] = await Promise.all([
    loadImage(qrTagSvg),
    loadImage(qrDataUrl),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasW, canvasH);
  ctx.drawImage(templateImg, 0, 0, canvasW, canvasH);
  ctx.drawImage(
    qrImg,
    Math.round(QR_PLACEHOLDER_X * scale),
    Math.round(QR_PLACEHOLDER_Y * scale),
    qrPx,
    qrPxH
  );

  // Re-overlay the mini Sideo logo from the template on top of the generated QR code.
  // The SVG template embeds the brand logo in the center of the QR placeholder area;
  // drawing the QR image covers it, so we clip-redraw just that region.
  const LOGO = { x: 79, y: 252, w: 49, h: 34 };
  ctx.save();
  ctx.beginPath();
  ctx.rect(
    Math.round(LOGO.x * scale),
    Math.round(LOGO.y * scale),
    Math.round(LOGO.w * scale),
    Math.round(LOGO.h * scale)
  );
  ctx.clip();
  ctx.drawImage(templateImg, 0, 0, canvasW, canvasH);
  ctx.restore();

  return canvas;
};

/**
 * @deprecated html2canvas approach replaced by buildBrandedCanvas.
 * Kept as no-op export to avoid breaking any direct imports.
 */
export const renderComponentToImage = async () => {
  throw new Error('renderComponentToImage is no longer supported. Use buildBrandedCanvas instead.');
};

/**
 * Download branded QR card as PNG
 */
export const downloadBrandedCard = async (qrId) => {
  try {
    const scale = 8; // ~1587×2948 px — good print quality
    const canvas = await buildBrandedCanvas(qrId, scale);

    // Add thin grey border + QR ID label below the card
    const borderPx = Math.round(3 * scale / 10); // 6px at scale 20
    const labelH = Math.round(60 * scale / 10);  // 120px at scale 20 — larger for readability
    const final = document.createElement('canvas');
    final.width = canvas.width + borderPx * 2;
    final.height = canvas.height + labelH + borderPx * 2;
    const ctx = final.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, final.width, final.height);
    // Border
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = borderPx;
    ctx.strokeRect(borderPx / 2, borderPx / 2, canvas.width + borderPx, canvas.height + borderPx);
    // Card
    ctx.drawImage(canvas, borderPx, borderPx);
    // QR ID label
    ctx.fillStyle = '#6b7280';
    ctx.font = `${Math.round(labelH * 0.55)}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText(qrId, final.width / 2, canvas.height + borderPx * 2 + labelH * 0.72);

    const link = document.createElement('a');
    link.href = final.toDataURL('image/png');
    link.download = `sideo-sticker-${qrId}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Failed to download branded card:', error);
    throw error;
  }
};

/**
 * Download multiple branded QR cards as a single ZIP file
 */
export const downloadBrandedCardsAsZip = async (qrIds, options = {}) => {
  const {
    fileName = 'sideo-branded-qr-codes.zip',
    onProgress = null,
  } = options;

  const scale = 8; // ~1587×2948 px — same resolution as single-download
  const zip = new JSZip();

  for (let i = 0; i < qrIds.length; i++) {
    const qrId = qrIds[i];
    const canvas = await buildBrandedCanvas(qrId, scale);

    // Replicate the same border + label post-processing as downloadBrandedCard
    const borderPx = Math.round(3 * scale / 10);
    const labelH = Math.round(60 * scale / 10);
    const final = document.createElement('canvas');
    final.width = canvas.width + borderPx * 2;
    final.height = canvas.height + labelH + borderPx * 2;
    const ctx = final.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, final.width, final.height);
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = borderPx;
    ctx.strokeRect(borderPx / 2, borderPx / 2, canvas.width + borderPx, canvas.height + borderPx);
    ctx.drawImage(canvas, borderPx, borderPx);
    ctx.fillStyle = '#6b7280';
    ctx.font = `${Math.round(labelH * 0.55)}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText(qrId, final.width / 2, canvas.height + borderPx * 2 + labelH * 0.72);

    // Convert canvas to blob and add to zip
    const blob = await new Promise((resolve) => final.toBlob(resolve, 'image/png'));
    zip.file(`sideo-sticker-${qrId}.png`, blob);

    if (onProgress) onProgress(i + 1, qrIds.length);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(zipBlob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Generate PDF with branded QR cards (1 per page for high quality)
 */
export const generateBrandedCardsPDF = async (qrCodes, options = {}) => {
  const {
    fileName = 'sideo-branded-cards.pdf'
  } = options;

  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    for (let i = 0; i < qrCodes.length; i++) {
      if (i > 0) {
        doc.addPage();
      }

      const qr = qrCodes[i];
      const canvas = await buildBrandedCanvas(qr.id, 6);
      const imageData = canvas.toDataURL('image/png');

      // Fill page height (reserve 8mm for QR ID text)
      const cardHeight = pageHeight - 8;
      const cardWidth = cardHeight * (TEMPLATE_VB_W / TEMPLATE_VB_H);
      const x = (pageWidth - cardWidth) / 2;

      doc.addImage(imageData, 'PNG', x, 0, cardWidth, cardHeight);
      // Thin grey border around the card
      doc.setDrawColor(209, 213, 219);
      doc.setLineWidth(0.3);
      doc.rect(x, 0, cardWidth, cardHeight);
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(16);
      doc.setFont('courier', 'normal');
      doc.text(qr.id, pageWidth / 2, cardHeight + 6, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
    }

    doc.save(fileName);
  } catch (error) {
    console.error('Failed to generate branded cards PDF:', error);
    throw error;
  }
};

/**
 * Generate QR code as data URL
 */
export const generateQRCodeImage = async (text, size = 200) => {
  try {
    return await QRCode.toDataURL(text, {
      errorCorrectionLevel: 'H',
      type: 'image/png',
      quality: 0.95,
      margin: 1,
      width: size,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
  } catch (error) {
    console.error('Failed to generate QR code:', error);
    throw error;
  }
};

/**
 * Download a single QR code as PNG
 */
export const downloadSingleQRCode = async (qrId, qrUrl) => {
  try {
    const dataUrl = await generateQRCodeImage(qrUrl);
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `QR-${qrId}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Failed to download QR code:', error);
    throw error;
  }
};

/**
 * Generate PDF with multiple QR codes in grid layout
 * Supports 4 per page (2x2), 6 per page (2x3), or 9 per page (3x3)
 */
export const generatePrintablePDF = async (qrCodes, options = {}) => {
  const {
    title = 'QR Codes',
    codesPerPage = 4,
    qrSize = 80,
    includeId = true,
    fileName = 'qr-codes.pdf'
  } = options;

  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;

    // Determine grid layout based on codesPerPage
    let cols, rows;
    if (codesPerPage === 4) {
      cols = 2;
      rows = 2;
    } else if (codesPerPage === 6) {
      cols = 2;
      rows = 3;
    } else {
      cols = 3;
      rows = 3;
    }

    const cellWidth = contentWidth / cols;
    const cellHeight = (pageHeight - margin * 2 - 30) / rows; // 30 for title

    let pageNum = 1;
    let codesOnCurrentPage = 0;
    let currentPage = 0;

    // Add title
    const addHeader = () => {
      doc.setFontSize(14);
      doc.text(title, margin, margin + 5);
      doc.setFontSize(10);
      doc.text(`Page ${pageNum}`, pageWidth - margin - 20, margin + 5);
      doc.setDrawColor(200);
      doc.line(margin, margin + 8, pageWidth - margin, margin + 8);
    };

    addHeader();

    for (let i = 0; i < qrCodes.length; i++) {
      const qrCode = qrCodes[i];

      // Add new page if needed
      if (codesOnCurrentPage >= codesPerPage) {
        doc.addPage();
        pageNum++;
        codesOnCurrentPage = 0;
        addHeader();
      }

      const row = Math.floor(codesOnCurrentPage / cols);
      const col = codesOnCurrentPage % cols;

      const x = margin + col * cellWidth;
      const y = margin + 15 + row * cellHeight;

      // Generate QR code image
      const qrImage = await generateQRCodeImage(qrCode.url, qrSize * 3.78); // Convert mm to pixels

      // Add QR code
      doc.addImage(qrImage, 'PNG', x + 5, y + 5, qrSize, qrSize);

      // Add QR ID below
      if (includeId) {
        doc.setFontSize(8);
        const idText = qrCode.id.substring(0, 20);
        const textWidth = doc.getStringUnitWidth(idText) * doc.internal.getFontSize() / doc.internal.pageSize.getWidth() * pageWidth;
        doc.text(idText, x + cellWidth / 2 - textWidth / 2, y + qrSize + 8);
      }

      codesOnCurrentPage++;
    }

    doc.save(fileName);
  } catch (error) {
    console.error('Failed to generate PDF:', error);
    throw error;
  }
};

/**
 * Generate printable HTML for direct printing (no PDF)
 * Returns JSX-like structure for React preview
 */
export const createPrintableLayout = (qrCodes, options = {}) => {
  const {
    codesPerPage = 4,
    title = 'QR Codes for Printing'
  } = options;

  let cols;
  if (codesPerPage === 4) cols = 2;
  else if (codesPerPage === 6) cols = 2;
  else cols = 3;

  const pages = [];
  for (let i = 0; i < qrCodes.length; i += codesPerPage) {
    pages.push(qrCodes.slice(i, i + codesPerPage));
  }

  return { pages, cols, title };
};

/**
 * Trigger browser print dialog for currently displayed element
 */
export const triggerPrint = (elementId = 'print-area') => {
  const printElement = document.getElementById(elementId);
  if (!printElement) {
    console.error(`Element with id '${elementId}' not found`);
    return;
  }

  const originalContent = document.body.innerHTML;
  document.body.innerHTML = printElement.innerHTML;
  window.print();
  document.body.innerHTML = originalContent;
  // Reload to restore event listeners
  window.location.reload();
};

/**
 * Generate a sheet format for label printing (A4 with multiple QRs)
 * Optimized for sticker label sheets
 */
export const generateLabelSheet = async (qrCodes, options = {}) => {
  const {
    includeId = true,
    fileName = 'qr-labels.pdf'
  } = options;

  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const stickerWidth = 100; // mm
    const stickerHeight = 60; // mm
    const columns = 2;
    const rows = 4;
    const horizontalGap = (pageWidth - columns * stickerWidth) / (columns + 1);
    const verticalGap = (pageHeight - rows * stickerHeight) / (rows + 1);
    const stickersPerPage = columns * rows;

    for (let i = 0; i < qrCodes.length; i++) {
      if (i > 0 && i % stickersPerPage === 0) {
        doc.addPage();
      }

      const qrCode = qrCodes[i];
      const indexOnPage = i % stickersPerPage;
      const row = Math.floor(indexOnPage / columns);
      const col = indexOnPage % columns;

      const x = horizontalGap + col * (stickerWidth + horizontalGap);
      const y = verticalGap + row * (stickerHeight + verticalGap);

      const canvas = await buildBrandedCanvas(qrCode.id, 1);
      const imageData = canvas.toDataURL('image/png');
      doc.addImage(imageData, 'PNG', x, y, stickerWidth, stickerHeight);
      // Thin grey border around the sticker
      doc.setDrawColor(209, 213, 219);
      doc.setLineWidth(0.2);
      doc.rect(x, y, stickerWidth, stickerHeight);

      if (includeId) {
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        const idText = qrCode.id.substring(0, 20);
        doc.text(idText, x, y + stickerHeight + 3);
      }
    }

    doc.save(fileName);
  } catch (error) {
    console.error('Failed to generate label sheet:', error);
    throw error;
  }
};
